import React from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PreApproveType } from '../../types/preloan/v3';
import { Partner } from '../LeadFormIndex/types';
import PreApproveCard from './PreApproveCard';

const CARD_WIDTH = 240;

type PreApproveCarouselProps = {
  preApproves: PreApproveType[];
  partner: Partner;
  token: string;
};

export default function PreApproveCarousel({
  preApproves,
  partner,
  token,
}: PreApproveCarouselProps) {
  const [selectedId, setSelectedId] = React.useState<string>(
    preApproves.length > 0 ? preApproves[0].id : '',
  );
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const isFirstCenter = React.useRef(true);
  const selectedIdRef = React.useRef(selectedId);
  selectedIdRef.current = selectedId;

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const centerSelected = (id: string, smooth = true) => {
    const el = itemRefs.current[id];
    if (el) {
      el.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  React.useEffect(() => {
    if (!selectedId) return;
    centerSelected(selectedId, !isFirstCenter.current);
    isFirstCenter.current = false;
    requestAnimationFrame(checkScroll);
  }, [selectedId]);

  React.useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return undefined;
    const onResize = () => {
      checkScroll();
      if (selectedIdRef.current) centerSelected(selectedIdRef.current, false);
    };
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [preApproves.length]);

  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'left' ? -260 : 260, behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {canScrollLeft && (
        <IconButton
          onClick={() => scroll('left')}
          size="small"
          sx={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            backgroundColor: 'background.paper',
            boxShadow: 2,
            '&:hover': { backgroundColor: 'background.paper' },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      <Box
        ref={scrollRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          gap: 2,
          pb: 1,
          px: preApproves.length > 1 ? `calc(50% - ${CARD_WIDTH / 2}px)` : 0.5,
          justifyContent: preApproves.length === 1 ? 'center' : undefined,
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          scrollSnapType: 'x mandatory',
        }}
      >
        {preApproves.map((preApprove) => (
          <Box
            key={preApprove.id}
            ref={(node: HTMLDivElement | null) => {
              itemRefs.current[preApprove.id] = node;
            }}
            sx={{ scrollSnapAlign: 'center', flexShrink: 0 }}
          >
            <PreApproveCard
              preApprove={preApprove}
              partner={partner}
              token={token}
              isSelected={selectedId === preApprove.id}
              onSelect={() => setSelectedId(preApprove.id)}
            />
          </Box>
        ))}
      </Box>
      {canScrollRight && (
        <IconButton
          onClick={() => scroll('right')}
          size="small"
          sx={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1,
            backgroundColor: 'background.paper',
            boxShadow: 2,
            '&:hover': { backgroundColor: 'background.paper' },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
}
